import React, { useLayoutEffect, useRef } from 'react';

import { Loader } from '@gravity-ui/uikit';
import { AudioProgress } from 'client/blocks/AudioProgress/AudioProgress';
import { PauseIcon } from 'client/icons/pause';
import { PlayIcon } from 'client/icons/play';
import { SwitchTrackIcon } from 'client/icons/switch-track';
import { TestAudioTrack } from 'client/types/skillTest';
import { formatSecondsForDuration } from 'client/utils/time';
import clamp from 'lodash/clamp';

import styles from './TestAudioItem.module.scss';

interface Props {
    track: TestAudioTrack;
    isPlaying?: boolean;
    duration?: number;
    currentTime?: number;
    isLoading?: boolean;
    preload?: boolean;
    onPlay?: () => void;
    onPause?: () => void;
    onSeek?: (currentTime: number) => void;
    onSeekEnd?: () => void;
    onNextTrack?: () => void;
    onPrevTrack?: () => void;
}

export const TestAudioItem: React.FC<Props> = ({
    track,
    duration = 0,
    currentTime = 0,
    isPlaying = false,
    isLoading = false,
    preload = false,
    onPause,
    onPlay,
    onSeek,
    onSeekEnd,
    onNextTrack,
    onPrevTrack,
}) => {
    const { title, logoUrl } = track;
    const audioRef = useRef<HTMLAudioElement>(null);

    const iconStyle: React.CSSProperties = {
        backgroundImage: `url(${logoUrl})`,
        backgroundSize: 'cover',
    };

    const durationString = formatSecondsForDuration(duration);
    const currentTimeString = formatSecondsForDuration(currentTime);

    const showProgress = duration > 0 && !isLoading;

    const ControlIcon = isPlaying ? PauseIcon : PlayIcon;
    const Icon = isLoading ? <Loader size="s" /> : <ControlIcon size="m" color="#fff" transparentControl />;

    const onIconClick = isPlaying ? onPause : onPlay;

    const innerDuration = duration === 0 ? 1 : duration;

    const preloadAudio = preload && Boolean(track);

    useLayoutEffect(() => {
        if (preloadAudio && audioRef.current) {
            audioRef.current.currentTime = track.offsetSec;
        }
    }, [track, preloadAudio]);

    return (
        <div className={styles.testAudioItem}>
            <div className={styles.testAudioItem__icon} style={iconStyle} onClick={onIconClick}>
                {Icon}
            </div>
            <div className={styles.testAudioItem__controls}>
                <div className={styles.testAudioItem__controlPrev} onClick={onPrevTrack}>
                    <SwitchTrackIcon type="prev" size="xs" />
                </div>
                <div className={styles.testAudioItem__controlNext} onClick={onNextTrack}>
                    <SwitchTrackIcon type="next" size="xs" />
                </div>
            </div>
            <div className={styles.testAudioItem__body}>
                <div className={styles.testAudioItem__titleWrapper}>
                    <div
                        className={`${styles.testAudioItem__title}${showProgress ? ` ${styles.testAudioItem__title_showProgress}` : ''}`}
                    >
                        {title}
                    </div>
                    {showProgress && (
                        <div className={styles.testAudioItem__time}>
                            {currentTimeString} / {durationString}
                        </div>
                    )}
                </div>
                {preloadAudio && <audio hidden src={track.url} key={track.url} preload="auto" ref={audioRef} />}
                {!isLoading && (
                    <div>
                        <AudioProgress
                            duration={innerDuration}
                            value={clamp(currentTime, 0, innerDuration)}
                            trackColorActive="#6839CF"
                            trackColorInactive="#C8CDD9"
                            onChange={onSeek}
                            onFinalChange={onSeekEnd}
                        />
                    </div>
                )}
            </div>
        </div>
    );
};
